import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import { SkillArchive } from './skill-archive';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useCompany } from '@/company/context';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { isWorkspacePageView, type WorkspacePageView } from './view-definitions';
import { WorkspaceUploadInput } from './upload-input';
import {
  dateLabel,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeading,
  sizeLabel,
  StartInChatButton,
} from './primitives';
import { KnowledgePage } from './knowledge';
import { ConnectionsPage } from './connections';
import { InboxPage } from './inbox';
import { audienceLabel } from './sharing-api';
import { useSkillLibrary, useWorkspaceFind } from './retrieval';
import { MeetingsPage } from './meetings';
import { AgentsPage } from './agents/page';
import {
  ResultThumbnail,
  RESULT_KIND_COPY_KEY,
  type ResultDisplayKind,
} from './result-presentation';
import type { CompanyResult, WorkspaceFindHit } from './types';
import { libraryLabel } from './living-result';
import { openLibraryTarget } from './artifact-panels';
import { workspaceResultCanvasTarget } from './artifact-panels/open-target';
import { LibraryFeedView } from './library-feed/library-feed-view';
import { useLibraryFeed } from './library-feed/use-library-feed';
import libraryFeedCopy from '@/i18n/ns/library-feed';
import { WORKSPACE_ATTACHMENT_LIMIT } from './skill-context';
import { emptyTurnContext } from './types';
import { ArtifactLibraryGate } from './artifact-panels/artifact-library-gate';
import { AppsLibraryGate } from './apps/apps-page';
import { AppIdeas } from './apps/app-create';
import { NEW_APP_CANVAS } from './apps/new-app';
import { focusComposer } from '@/components/assistant-ui/action-ask-again';
import { shouldAutoFocusComposer } from '@/lib/mobile';
import { TemplateGallery } from './company-feed';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import skillsCopy from '@/i18n/ns/skills';
import { formatCount } from '@/i18n/format';
import { handOffAppsSearch } from './apps/apps-search-handoff';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import appsNavCopy from '@/i18n/ns/apps-nav';
import searchCopy from '@/i18n/ns/search';
import { Snippet } from './finder';
import { searchSnippet } from './finder-results';
import { useIntlLocale } from './intl-locale';
import { StorefrontPage } from './storefront/storefront-page';
import { ProjectsPage } from './projects/projects-page';

function resultKind(hit: WorkspaceFindHit): ResultDisplayKind {
  const kind = hit.metadata?.resultKind ?? hit.sourceKind;
  // A saved App keeps its own kind; it is never shown as a Document.
  return kind === 'table' ||
    kind === 'chart' ||
    kind === 'deck' ||
    kind === 'html' ||
    kind === 'app'
    ? kind
    : 'markdown';
}

function hitAudience(hit: WorkspaceFindHit, companyName: string, locale: string) {
  const visibility = hit.metadata?.visibility;
  if (visibility !== 'private' && visibility !== 'members' && visibility !== 'company') return '';
  return audienceLabel({ visibility }, companyName, locale);
}

// Living Results: "Edited by …" or "Kept up to date by …" under the title.
function hitRevisionLabel(
  hit: WorkspaceFindHit,
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const { revision, maintainedBy } = (hit.metadata ?? {}) as Pick<
    CompanyResult,
    'revision' | 'maintainedBy'
  >;
  return libraryLabel({ revision, maintainedBy }, userId, uiText);
}

function hitLineage(hit: WorkspaceFindHit, uiText: WorkspaceText = workspaceTextFor('en')) {
  if (!hit.metadata?.fromSkill) return uiText('Saved result');
  const name = hit.metadata.skillName;
  return typeof name === 'string' && name
    ? uiText(hit.metadata.runMode === 'preview' ? 'Preview of {name}' : 'Made with {name}', {
        name,
      })
    : uiText('Made with a Skill');
}

const pageByView: Record<WorkspacePageView, ComponentType> = {
  projects: ProjectsPage,
  results: LibraryPage,
  apps: AppsPage,
  storefront: StorefrontPage,
  knowledge: KnowledgePage,
  connections: ConnectionsPage,
  meetings: MeetingsPage,
  skills: SkillsPage,
  agents: AgentsPage,
};

export function WorkspacePages() {
  const ws = useWorkspace();
  const Page =
    ws.view === 'inbox'
      ? InboxPage
      : isWorkspacePageView(ws.view)
        ? pageByView[ws.view]
        : LibraryPage;
  return (
    <section className="ws-page">
      {ws.error ? (
        <ErrorState message={ws.error} onRetry={() => void ws.refresh()} />
      ) : ws.loading &&
        !['files', 'results'].includes(ws.view) &&
        !ws.data.files.length &&
        !ws.data.skills.length ? (
        <LoadingState />
      ) : (
        <Page />
      )}
    </section>
  );
}
export function UploadButton({ label, primary = false }: { label?: string; primary?: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <WorkspaceUploadInput
        ref={input}
        label={label ?? uiText('Upload a file')}
        disabled={ws.busy}
        onFiles={(files) => ws.upload(files)}
      />
      <button
        className={`ws-button ${primary ? 'primary' : ''}`}
        onClick={() => input.current?.click()}
        disabled={ws.busy}
      >
        <WorkspaceIcon name="plus" size={16} />
        {ws.busy ? uiText('Uploading…') : (label ?? uiText('Upload a file'))}
      </button>
    </>
  );
}
// A search row's excerpt is the Search snippet (DESIGN-SPEC §3.4): the words of the
// match with the searched words marked, never raw markdown, JSON or CSV.
function SearchExcerpt({ hit, query }: { hit: WorkspaceFindHit; query: string }) {
  const copy = useNamespaceCopy(searchCopy);
  const locale = useIntlLocale();
  if (!query.trim()) return null;
  return (
    <Snippet
      snippet={searchSnippet(hit, query, locale)}
      copy={copy}
      locale={locale}
      className="ws-search-snippet"
    />
  );
}
/** The Apps place, as Skills: the Library's authorized App list as cards (a card opens the genuine saved App), and
 * making one starts in chat: a new chat with the App panel waiting beside it and the words in the composer, never sent. */
function AppsPage() {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const nav = useNamespaceCopy(appsNavCopy);
  // On a desktop the caret then waits after the words, also when the open chat is still empty and so is reused.
  const create = (draft: string) => {
    ws.send(draft, undefined, false, NEW_APP_CANVAS, { newChat: true });
    if (shouldAutoFocusComposer()) focusComposer();
  };
  // "Build me an App that " ends in a space, so the caret waits right after the words.
  const start = () => create(`${nav.appsCreateDraft} `);
  return (
    <div className="ws-library-page">
      <PageHeading
        eyebrow={uiText('{company} workspace', { company: ws.companyName })}
        title={copy.libraryKindApps}
        description={nav.appsDescription}
      >
        <StartInChatButton label={nav.appsCreateInChat} onClick={start} />
      </PageHeading>
      <AppsLibraryGate
        onOpen={(target) => openLibraryTarget(ws, target)}
        onMake={start}
        viewerName={ws.user?.displayName ?? null}
      />
      <AppIdeas onSelect={create} />
    </div>
  );
}

/**
 * The Library: ONE authorized list (results, decks, sheets, files) with a kind filter, an owner filter and search,
 * from the server's single Library feed. Adding files lives in Library → Files: its first row, a drop target over
 * the page, and ⌘U. A server that does not serve the feed yet keeps the three-tab list below, unchanged.
 */
function LibraryPage() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const copy = useNamespaceCopy(libraryFeedCopy);
  const onScreen = ws.view === 'results' || ws.view === 'files';
  // A save elsewhere (a Chat save refreshes the workspace results) reads the list again while it is on screen.
  const feed = useLibraryFeed(ws.view === 'files' ? 'files' : 'all', onScreen, ws.data.results);
  if (feed.load === 'unavailable') return <LegacyLibrary />;
  return (
    <div className="ws-library-page">
      {/* The breadcrumb names the place; the shared page title and one line say what the Library holds. */}
      <PageHeading title={panels.libraryTitle} description={copy.description} />
      <LibraryFeedView
        feed={feed}
        onScreen={onScreen}
        viewerName={ws.user?.displayName ?? null}
        onUseInChat={
          ws.threadSaved && ws.turnContext.fileIds.length < WORKSPACE_ATTACHMENT_LIMIT
            ? (fileId) => {
                // A reader never writes into someone else's chat: the file starts a chat of their own.
                if (ws.readOnly) return ws.send('', { ...emptyTurnContext(), fileIds: [fileId] });
                // Otherwise it joins what the open chat is writing: its words and attachments stay.
                if (!ws.turnContext.fileIds.includes(fileId))
                  ws.setTurnContext({
                    ...ws.turnContext,
                    fileIds: [...ws.turnContext.fileIds, fileId],
                  });
                ws.navigate('chat');
              }
            : undefined
        }
        onMake={() =>
          ws.send(
            uiText(
              'Help me create a useful report from our company knowledge and available sources.',
            ),
          )
        }
        onOpenApps={(query) => {
          handOffAppsSearch(query);
          ws.navigate('apps');
        }}
        onOpen={(target) =>
          target.kind === 'artifact'
            ? openLibraryTarget(ws, {
                artifactId: target.id,
                revisionId: target.revisionId,
                versionId: target.versionId,
                versionNumber: target.versionNumber,
                purpose: target.purpose,
              } as Parameters<typeof openLibraryTarget>[1])
            : void ws.openCanvas({ kind: target.kind, id: target.id })
        }
      />
    </div>
  );
}

function LegacyLibrary() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const company = useCompany();
  const [query, setQuery] = useState('');
  const [openError, setOpenError] = useState('');
  const openGeneration = useRef(0);
  useEffect(() => {
    ++openGeneration.current;
    setOpenError('');
    return () => {
      ++openGeneration.current;
    };
  }, [ws.user?.id, company.data?.company?.id, ws.view, ws.libraryTab]);
  const openResult = async (hit: WorkspaceFindHit) => {
    const generation = ++openGeneration.current;
    setOpenError('');
    try {
      const target = await workspaceResultCanvasTarget(hit);
      if (generation !== openGeneration.current) return;
      if (!target || !ws.openCanvas(target)) throw new Error('saved_work_unavailable');
    } catch {
      if (generation === openGeneration.current)
        setOpenError(uiText('This saved work couldn’t be opened. Open it again to retry.'));
    }
  };
  // The Artifacts tab is the authorized Artifact Library (ART/SHARE API); Results and Files
  // below are the legacy library and stay exactly as they were.
  const artifactsTab = ws.libraryTab === 'artifacts';
  const kindCopy = useNamespaceCopy(artifactPanelsCopy);
  const files = ws.view === 'files';
  // WorkspacePages stays mounted while Chat is in front (this page is also the fallback for the Chat view), so
  // the authorized list is read only while Results or Files is actually on screen: entering Library asks for a
  // fresh page, and the existing refresh token follows the loaded results/files so a save elsewhere (a copy
  // saved from Chat refreshes them) shows up without a reload.
  const onScreen = ws.view === 'results' || ws.view === 'files';
  // The Artifacts tab reads its own authorized list.
  const retrieval = useWorkspaceFind(
    onScreen && !artifactsTab && Boolean(ws.user?.id && company.data?.company?.id),
    query,
    files ? ['file'] : ['result'],
    `${ws.user?.id ?? ''}\0${company.data?.company?.id ?? ''}`,
    files ? ws.data.files : ws.data.results,
  );
  // Apps are not Library items (they live in Apps): a saved App is never listed here, so there is nothing here
  // that cannot be opened. When a search matches some, the list ends with a line that goes to them.
  const items = retrieval.hits.filter((hit) => files || resultKind(hit) !== 'app');
  const appMatches = files ? 0 : retrieval.hits.length - items.length;
  return (
    <div className="ws-library-page">
      <PageHeading
        eyebrow={uiText('{company} workspace', { company: ws.companyName })}
        title={uiText('Library')}
        description={
          files
            ? uiText('Source material for your work. Available whenever you need it.')
            : uiText('The work you want to keep, with its sources and conversation attached.')
        }
      >
        {files && <UploadButton primary />}
      </PageHeading>
      <div className="ws-view-tabs">
        <button
          className={!files && !artifactsTab ? 'active' : ''}
          aria-current={!files && !artifactsTab ? 'page' : undefined}
          onClick={() => {
            ws.navigate('results');
          }}
        >
          {uiText('Results')}
        </button>
        <button
          className={files && !artifactsTab ? 'active' : ''}
          aria-current={files && !artifactsTab ? 'page' : undefined}
          onClick={() => {
            ws.navigate('files');
          }}
        >
          {uiText('Files')}
        </button>
        <button
          className={artifactsTab ? 'active' : ''}
          aria-current={artifactsTab ? 'page' : undefined}
          onClick={() => ws.navigate('results', 'artifacts')}
        >
          {uiText('Artifacts')}
        </button>
      </div>
      {artifactsTab && (
        <ArtifactLibraryGate
          onOpen={(target) => openLibraryTarget(ws, target)}
          onMake={() => ws.newChat()}
        />
      )}
      {!artifactsTab && (
        <>
          <input
            className="ws-resource-search"
            aria-label={uiText('Search library')}
            placeholder={
              files ? uiText('Find files by meaning…') : uiText('Find saved work by meaning…')
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {retrieval.error && <ErrorState message={retrieval.error} onRetry={retrieval.retry} />}
          {openError && <ErrorState message={openError} />}
          {retrieval.degraded && (
            <p className="ws-page-note" role="status">
              {uiText(
                'Search coverage is limited while indexing or meaning-based search is unavailable. You can retry or browse recent items.',
              )}
            </p>
          )}
          {!items.length && !retrieval.loading && !retrieval.error ? (
            <EmptyState
              icon={files ? 'files' : 'results'}
              title={
                query.trim()
                  ? uiText('No matching work found.')
                  : files
                    ? uiText('A starting point for your next question.')
                    : uiText('Good work is worth keeping.')
              }
              description={
                query.trim()
                  ? uiText('Try a different description or clear the search to browse.')
                  : files
                    ? uiText(
                        'Upload source material once and use it in conversations and Skills. Your original stays in the library.',
                      )
                    : uiText(
                        'Ask Poneva to save an answer, table or chart. Results stay connected to the conversation and their sources.',
                      )
              }
            >
              {!files && (
                <button
                  className="ws-button primary"
                  onClick={() =>
                    ws.send(
                      uiText(
                        'Help me create a useful report from our company knowledge and available sources.',
                      ),
                    )
                  }
                >
                  {uiRich('Start in chat {value1}', {
                    value1: <WorkspaceIcon name="arrow" size={14} />,
                  })}
                </button>
              )}
            </EmptyState>
          ) : (
            <>
              <div
                className={`ws-library-list ${files ? 'ws-library-files' : 'ws-library-results'}`}
              >
                {files
                  ? items.map((hit) => (
                      <button
                        className="ws-record"
                        key={hit.id}
                        onClick={() => ws.openCanvas({ kind: 'file', id: hit.id })}
                      >
                        <span className="ws-record-symbol">
                          <WorkspaceIcon name="file" size={22} />
                        </span>
                        <span className="ws-record-copy">
                          <strong>{hit.title}</strong>
                          <small>
                            {typeof hit.metadata?.sizeBytes === 'number'
                              ? `${sizeLabel(hit.metadata.sizeBytes)} · `
                              : ''}
                            {hitAudience(hit, ws.companyName, uiText.locale)}
                          </small>
                          <SearchExcerpt hit={hit} query={query} />
                        </span>
                        <span className="ws-record-date">
                          {hit.updatedAt ? dateLabel(hit.updatedAt, uiText.locale) : ''}
                        </span>
                        <WorkspaceIcon name="chevron" size={16} />
                      </button>
                    ))
                  : items.map((hit) => (
                      <button
                        className="ws-library-result"
                        key={hit.id}
                        aria-label={uiText('Open result: {value1}', { value1: hit.title })}
                        aria-current={
                          (ws.canvas?.kind === 'result' || ws.canvas?.kind === 'artifact') &&
                          ws.canvas.id === hit.id
                            ? 'true'
                            : undefined
                        }
                        onClick={() => void openResult(hit)}
                      >
                        <ResultThumbnail kind={resultKind(hit)} />
                        <span className="ws-library-result-copy">
                          <strong>{hit.title}</strong>
                          <span>
                            {kindCopy[RESULT_KIND_COPY_KEY[resultKind(hit)]]} ·{' '}
                            {hitLineage(hit, uiText)}
                          </span>
                          <SearchExcerpt hit={hit} query={query} />
                          <small>
                            {hitAudience(hit, ws.companyName, uiText.locale)} ·{' '}
                            {hit.updatedAt ? dateLabel(hit.updatedAt, uiText.locale) : ''}
                          </small>
                          {hitRevisionLabel(hit, ws.user?.id, uiText) && (
                            <small className="ws-library-revision">
                              {hitRevisionLabel(hit, ws.user?.id, uiText)}
                            </small>
                          )}
                        </span>
                        <WorkspaceIcon name="chevron" size={16} />
                      </button>
                    ))}
              </div>
            </>
          )}
          {appMatches > 0 && query.trim() && (
            <button
              type="button"
              className="ws-library-apps-match"
              data-testid="library-apps-match"
              onClick={() => {
                handOffAppsSearch(query);
                ws.navigate('apps');
              }}
            >
              {formatCount(kindCopy.libraryAppsMatchNoun, appMatches, locale)}
            </button>
          )}
          {retrieval.loading && <LoadingState label={uiText('Loading more library items…')} />}
          {!retrieval.loading && retrieval.hasMore && (
            <button className="ws-button" onClick={retrieval.loadMore}>
              {uiText('Load more')}
            </button>
          )}
          {files && (
            <p className="ws-page-note">
              {uiText(
                'Documents, transcripts, audio and video · Up to 500 MB per file. New files are private. Choose their audience with Share and which processable files become searchable company knowledge.',
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}
function SkillsPage() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const copy = useNamespaceCopy(skillsCopy);
  const [query, setQuery] = useState('');
  const scope = `${ws.user?.id ?? ''}\0${company.data?.company?.id ?? ''}`;
  const library = useSkillLibrary(!query.trim(), scope, ws.data.skills);
  const retrieval = useWorkspaceFind(
    Boolean(query.trim()),
    query,
    ['skill'],
    scope,
    ws.data.skills,
  );
  return (
    <>
      <PageHeading
        eyebrow={uiText('{company} workspace', { company: ws.companyName })}
        title={uiText('Skills')}
        description={copy.pageLead}
      >
        <StartInChatButton label={uiText('Create in chat')} onClick={() => ws.authorSkill()} />
      </PageHeading>
      <input
        className="ws-resource-search"
        aria-label={uiText('Search Skills')}
        placeholder={copy.search}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {query.trim() ? (
        <>
          <p className="ws-page-note">
            {uiText('Search finds published Skills. Clear the search to browse your drafts too.')}
          </p>
          {retrieval.error && <ErrorState message={retrieval.error} onRetry={retrieval.retry} />}
          {retrieval.degraded && (
            <p role="status" className="ws-page-note">
              {uiText(
                'Search coverage is limited while indexing or meaning-based search is unavailable.',
              )}
            </p>
          )}
          {retrieval.hits.map((hit) => (
            <button
              className="ws-record"
              key={hit.id}
              onClick={() => ws.openCanvas({ kind: 'skill', id: hit.id })}
            >
              <span className="ws-record-copy">
                <strong>{hit.title}</strong>
                <SearchExcerpt hit={hit} query={query} />
              </span>
              <WorkspaceIcon name="chevron" size={16} />
            </button>
          ))}
          {retrieval.loading && <LoadingState label={uiText('Searching Skills…')} />}
          {!retrieval.loading && !retrieval.error && !retrieval.hits.length && (
            <p className="ws-page-note">
              {uiText('No matching published Skills found. Try a different description.')}
            </p>
          )}
          {!retrieval.loading && retrieval.hasMore && (
            <button className="ws-button" onClick={retrieval.loadMore}>
              {uiText('Load more Skills')}
            </button>
          )}
        </>
      ) : (
        <>
          {library.error && <ErrorState message={library.error} onRetry={library.retry} />}
          {!library.skills.length && !library.loading && !library.error ? (
            <EmptyState icon="skills" title={copy.emptyTitle} description={copy.emptyBody}>
              <StartInChatButton next label={copy.emptyAction} onClick={() => ws.authorSkill()} />
            </EmptyState>
          ) : (
            <div className="ws-skill-list">
              {library.skills.map((listedSkill) => {
                // A draft saved in the open canvas reaches workspace state
                // immediately; the paged library snapshot may still be older.
                const updated = ws.data.skills.find((item) => item.id === listedSkill.id);
                const skill =
                  updated && updated.currentRevision > listedSkill.currentRevision
                    ? updated
                    : listedSkill;
                const name = skill.draft.name;
                const description = skill.draft.description;
                const published = skill.publishedRevision !== null;
                return (
                  <article className="ws-skill-row" key={skill.id}>
                    <button
                      className="ws-skill-main"
                      aria-label={uiText('View Skill: {value1}', { value1: name })}
                      onClick={() => ws.openCanvas({ kind: 'skill', id: skill.id })}
                    >
                      <span className="ws-skill-card-top">
                        <span className="ws-skill-symbol">
                          <WorkspaceIcon name="skills" size={23} />
                        </span>
                        <span className="ws-skill-category">
                          {published
                            ? uiText('Everyone at {value1}', { value1: ws.companyName })
                            : skill
                              ? audienceLabel(skill, ws.companyName, uiText.locale)
                              : uiText('Company workspace')}
                        </span>
                      </span>
                      <strong>{name}</strong>
                      <p>{description}</p>
                    </button>
                    <footer className="ws-skill-card-footer">
                      <span className="ws-skill-availability">
                        <span className={`ws-skill-dot${published ? ' available' : ''}`} />
                        {published ? copy.cardApplies : copy.cardDraft}
                      </span>
                      <button
                        className="ws-text-button ws-skill-version"
                        aria-label={uiText('View {value1}', { value1: name })}
                        onClick={() => ws.openCanvas({ kind: 'skill', id: skill.id })}
                      >
                        {formatCopy(copy.cardVersion, { revision: skill.currentRevision })}
                        {skill.publishedRevision !== null &&
                          skill.currentRevision !== skill.publishedRevision &&
                          ` · ${copy.cardNewDraft}`}
                        <WorkspaceIcon name="arrow" size={14} />
                      </button>
                    </footer>
                  </article>
                );
              })}
            </div>
          )}
          {library.loading && <LoadingState label={uiText('Loading Skills…')} />}
          {!library.loading && library.hasMore && (
            <button className="ws-button" onClick={library.loadMore}>
              {uiText('Load more Skills')}
            </button>
          )}
          {/* Ready-made commerce Skills (collaboration v1). */}
          {!library.loading && <TemplateGallery />}
        </>
      )}
      <p className="ws-page-note">{copy.pageNote}</p>
      <SkillArchive key={scope} />
    </>
  );
}
